/**
 * Strata Relay — global unread state (Phase 5).
 * Fetches /relay/unread and keeps it fresh via the shared SSE stream, so both
 * the sidebar badge (Layout) and the room list (RelayPage) read one source.
 */
import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getUnread, markRoomRead as apiMarkRead } from '@/api/relay';
import { subscribeRelay } from '@/api/relayStream';
import { handleIncomingRelayMessage, initRelayAudioUnlock } from '@/lib/relayAlerts';

const EMPTY = { total: 0, total_mentions: 0, rooms: {} };
const Ctx = createContext({ unread: EMPTY, refresh: () => {}, markRoomRead: () => {}, setActiveRoom: () => {} });

export const useRelayUnread = () => useContext(Ctx);

export function RelayUnreadProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const enabled = Boolean(user?.relay_enabled);
  const [unread, setUnread] = useState(EMPTY);
  const debounceRef = useRef(null);
  const activeRoomRef = useRef(null);

  // The Relay page reports which room is on-screen so we can suppress
  // the chime/notification for messages the user is already watching.
  const setActiveRoom = useCallback((roomId) => { activeRoomRef.current = roomId || null; }, []);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await getUnread();
      setUnread(data || EMPTY);
    } catch { /* transient */ }
  }, [enabled]);

  const debouncedRefresh = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(refresh, 600);
  }, [refresh]);

  const markRoomRead = useCallback(async (roomId) => {
    if (!enabled || !roomId) return;
    setUnread((u) => {
      if (!u.rooms[roomId]) return u;
      const rooms = { ...u.rooms };
      const removed = rooms[roomId];
      delete rooms[roomId];
      return {
        total: Math.max(0, u.total - (removed?.unread || 0)),
        total_mentions: Math.max(0, u.total_mentions - (removed?.mentions || 0)),
        rooms,
      };
    });
    try { await apiMarkRead(roomId); } catch { /* ignore */ }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) { setUnread(EMPTY); return undefined; }
    refresh();
    initRelayAudioUnlock();
    const unsub = subscribeRelay((evt) => {
      if (evt.type === 'message.new') {
        debouncedRefresh();
        handleIncomingRelayMessage(evt, {
          myId: user?.id,
          activeRoomId: activeRoomRef.current,
          navigate,
        });
      } else if (evt.type === 'room.new') {
        debouncedRefresh();
      }
    });
    return unsub;
  }, [enabled, refresh, debouncedRefresh, user?.id, navigate]);

  return <Ctx.Provider value={{ unread, refresh, markRoomRead, setActiveRoom }}>{children}</Ctx.Provider>;
}
